import React, { useState } from 'react';
import {
  BtnDisabled,
  BtnModalTable,
  BtnOutlineGreen,
  BtnOutlineRed,
} from '../../../styled-components';
import {
  Col,
  CustomInput,
  Form,
  FormGroup,
  FormText,
  Input,
  InputGroup,
  InputGroupAddon,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import DateInput from '../../../commons/DateInput';
import AutoSuggest from '../../../commons/Autosuggest';
import { useTranslation } from 'react-i18next';
import { ADMIN_TYPES } from '../../../App';
import moment from 'moment';

const ModalEditInsurance = ({
  searchAdministrators,
  searchEstateAgents,
  searchCommercials,
  fetchAdminCondos,
  fetchAdminCustomers,
  condos,
  customersExtra,
  onConfirm,
  data: insurance = {},
}) => {
  const [t] = useTranslation('INSURANCES');
  const [modal, setModal] = useState(false);

  const getInitialFormState = () => ({
    branch: insurance.branch || '',
    policy_code: insurance.policy_code || '',
    insurance_code: insurance.insurance_code || '',
    fraz: insurance.fraz || '',
    start_date: insurance.start_date || '',
    end_date: insurance.end_date || '',
    gross: insurance.gross || '',
    net: insurance.net || '',
    shareToInvoice: insurance.shareToInvoice || '',
    subAgencyPerc: insurance.subAgencyPerc || '',
    admin: ADMIN_TYPES.includes(insurance.admin?.type)
      ? insurance.admin
      : { _id: '', businessName: '' },
    estateAgent: ['AGENCY'].includes(insurance.admin?.type)
      ? insurance.admin
      : { _id: '', businessName: '' },
    commercial: ['COMMERCIAL'].includes(insurance.admin?.type)
      ? insurance.admin
      : { _id: '', businessName: '' },
    customer: insurance.customer._id,
    customerType: insurance.customerType || '',
    customerId: insurance.customerId || '',
  });
  const [formValues, setFormValues] = useState(getInitialFormState());
  const [formErrors, setFormErrors] = useState({
    branch: false,
    policy_code: false,
    insurance_code: false,
    fraz: false,
    start_date: false,
    end_date: false,
    gross: false,
    net: false,
    shareToInvoice: false,
    subAgencyPerc: false,
    admin: false,
    estateAgent: false,
    commercial: false,
    customer: false,
    notCorrectDate: false,
  });

  const toggle = (e) => {
    setModal(!modal);
    setFormValues(getInitialFormState());

    fetchAdminCondos(insurance.admin._id, insurance.start_date);
    fetchAdminCustomers(insurance.admin._id);
  };

  function onInputChange({ target }) {
    setFormValues((state) => ({ ...state, [target.name]: target.value }));
  }

  function onNumericInputChange({ target }) {
    if (/^[\d]*\.?[\d]{0,2}$/.test(target.value)) {
      setFormValues((state) => ({
        ...state,
        [target.name]: target.value,
      }));
    }
  }

  const renderAdminSuggestion = ({ businessName = '' }) => businessName;
  const getAdminSuggestionValue = ({ businessName = '' }) => businessName;
  function onAdminSuggestionSelect({ _id, businessName }) {
    setFormValues((state) => ({
      ...state,
      admin: { _id, businessName },
    }));

    fetchAdminCondos(_id, start_date);
    fetchAdminCustomers(_id);
  }

  function onEstateAgentsSuggestionSelect({ _id, businessName }) {
    setFormValues((state) => ({
      ...state,
      estateAgent: { _id, businessName },
    }));

    fetchAdminCondos(_id, start_date);
    fetchAdminCustomers(_id);
  }

  function onCommercialsSuggestionSelect({ _id, businessName }) {
    setFormValues((state) => ({
      ...state,
      commercial: { _id, businessName },
    }));

    fetchAdminCondos(_id, start_date);
    fetchAdminCustomers(_id);
  }

  const onEditInsuranceConfirm = () => {
    const {
      customer,
      admin,
      estateAgent,
      commercial,
      branch,
      policy_code,
      insurance_code,
      fraz,
      start_date,
      end_date,
      gross,
      net,
      shareToInvoice,
      subAgencyPerc,
    } = formValues;

    const errors = {};
    if (!customer || customer === '') errors.customer = true;
    if (!admin._id && !estateAgent._id && !commercial._id) errors.admin = true;
    if (!estateAgent._id && !admin._id && !commercial._id)
      errors.estateAgent = true;
    if (!commercial._id && !admin._id && !estateAgent._id)
      errors.commercial = true;
    if (!branch) errors.branch = true;
    if (!policy_code) errors.policy_code = true;
    if (!insurance_code) errors.insurance_code = true;
    if (!fraz) errors.fraz = true;
    if (!start_date) errors.start_date = true;
    if (!end_date) errors.end_date = true;
    if (!gross) errors.gross = true;
    if (!net) errors.net = true;
    if (!shareToInvoice) errors.shareToInvoice = true;
    if (!subAgencyPerc) errors.subAgencyPerc = true;

    if (
      start_date &&
      !moment
        .utc(start_date)
        .startOf('day')
        .isSame(moment.utc(insurance.start_date).startOf('day'))
    ) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const firstFebruary = new Date(currentYear, 1, 1);
      const inputDate = new Date(start_date);

      if (
        currentDate >= firstFebruary &&
        inputDate.getFullYear() < currentYear
      ) {
        errors.notCorrectDate = true;
      } else if (
        currentDate < firstFebruary &&
        inputDate.getFullYear() === currentYear - 1
      ) {
      } else if (inputDate < new Date(currentYear - 1, 11, 31)) {
        errors.notCorrectDate = true;
      }
    }

    if (Object.keys(errors).length > 0) {
      return setFormErrors(errors);
    }

    onConfirm({
      ...formValues,
      _id: insurance._id,
      admin: formValues.admin._id,
      estateAgent: formValues.estateAgent._id,
      commercial: formValues.commercial._id,
      net: parseFloat(formValues.net),
      gross: parseFloat(formValues.gross),
      shareToInvoice: parseFloat(formValues.shareToInvoice),
      subAgencyPerc: parseFloat(formValues.subAgencyPerc),
    });

    toggle();
  };

  function listenForEscapeEvent() {
    document.addEventListener('keydown', ({ key }) => {
      if (key === 'Escape') toggle();
    });
  }

  const onSelectCondo = (selectedCondoId) => {
    if (selectedCondoId === '') {
      setFormValues((state) => ({
        ...state,
        customer: '',
        vatCode: '',
        customerType: '',
        customerId: '',
      }));
      formErrors.customer = true;
      return;
    }

    formErrors.customer = false;
    const selectedCondo = condos.filter(
      (condo) => condo._id === selectedCondoId
    )[0];

    setFormValues((state) => ({
      ...state,
      customer: selectedCondo.name,
      vatCode: selectedCondo.vatCode,
      customerType: 'condos',
      customerId: selectedCondo._id,
    }));
  };

  const onSelectCustomerExtra = (selectedCustomerExtraId) => {
    if (selectedCustomerExtraId === '') {
      setFormValues((state) => ({
        ...state,
        customer: '',
        vatCode: '',
        customerType: '',
        customerId: '',
      }));
      formErrors.customerExtra = true;
      return;
    }

    formErrors.customerExtra = false;
    const selectedCustomerExtra = customersExtra.filter(
      (customer) => customer._id === selectedCustomerExtraId
    )[0];

    setFormValues((state) => ({
      ...state,
      customer: selectedCustomerExtra.name,
      vatCode: selectedCustomerExtra.taxCode,
      customerType: 'customerExtra',
      customerId: selectedCustomerExtra._id,
    }));
  };

  const {
    admin,
    estateAgent,
    commercial,
    customer,
    branch,
    policy_code,
    insurance_code,
    fraz,
    start_date,
    end_date,
    gross,
    net,
    shareToInvoice,
    subAgencyPerc,
  } = formValues;

  return (
    <React.Fragment>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <div style={{ marginLeft: '10px' }}>
          {/* {insurance.panoramicaInvoiceId !== undefined ? (
            <BtnDisabled disabled type="button" onClick={toggle}>
              MODIFICA
            </BtnDisabled>
          ) : ( */}
            <BtnModalTable type="button" onClick={toggle}>
              MODIFICA
            </BtnModalTable>
          {/* )} */}
        </div>
      </div>

      <Modal
        isOpen={modal}
        className="new-invoice-modal"
        onOpened={listenForEscapeEvent}
        style={{ maxWidth: '80%' }}
      >
        <ModalHeader toggle={toggle}>{t('EDIT_INSURANCE')}</ModalHeader>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            onEditInsuranceConfirm(formValues);
          }}
        >
          <ModalBody>
            <Row>
              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('BRANCH')} *</Label>
                  <Input
                    type="text"
                    name="branch"
                    value={branch}
                    onChange={onInputChange}
                    disabled={insurance.panoramicaInvoiceId !== undefined}
                  />
                  {formErrors.branch && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('POLICY_CODE')} *</Label>
                  <Input
                    type="text"
                    name="policy_code"
                    value={policy_code}
                    onChange={onInputChange}
                  />
                  {formErrors.policy_code && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('INSURANCE_CODE')} *</Label>
                  <Input
                    type="text"
                    name="insurance_code"
                    value={insurance_code}
                    onChange={onInputChange}
                    disabled={insurance.panoramicaInvoiceId !== undefined}
                  />
                  {formErrors.insurance_code && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('FRAZ')} *</Label>
                  <Input
                    type="text"
                    name="fraz"
                    value={fraz}
                    onChange={onInputChange}
                    disabled={insurance.panoramicaInvoiceId !== undefined}
                  />
                  {formErrors.fraz && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('START_DATE')} *</Label>
                  <DateInput
                    name="start_date"
                    value={start_date}
                    disabled={insurance.panoramicaInvoiceId !== undefined}
                    onChange={(start_date) => {
                      const utcDate = moment
                        .utc(moment(start_date).format('YYYY-MM-DD'))
                        .startOf('day')
                        .toDate();
                      setFormValues((state) => ({
                        ...state,
                        start_date: utcDate,
                        admin: { _id: '', businessName: '' },
                        estateAgent: { _id: '', businessName: '' },
                        commercial: { _id: '', businessName: '' },
                        customer: '',
                        customerType: '',
                        customerId: '',
                      }));
                    }}
                  />
                  {formErrors.start_date && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                  {formErrors.notCorrectDate && (
                    <FormText className="error-message">
                      {t('DATE_NOT_COMPLIANT')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('END_DATE')} *</Label>
                  <DateInput
                    name="end_date"
                    value={end_date}
                    disabled={insurance.panoramicaInvoiceId !== undefined}
                    onChange={(end_date) => {
                      const utcDate = moment
                        .utc(moment(end_date).format('YYYY-MM-DD'))
                        .startOf('day')
                        .toDate();
                      setFormValues((state) => ({
                        ...state,
                        end_date: utcDate,
                      }));
                    }}
                  />
                  {formErrors.end_date && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('CONTACTS:IMPORT_TO_RECEIVE')} *</Label>
                  <InputGroup>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      name="gross"
                      value={gross}
                      onChange={onNumericInputChange}
                      disabled={insurance.panoramicaInvoiceId !== undefined}
                    />
                    <InputGroupAddon addonType="prepend">€</InputGroupAddon>
                  </InputGroup>
                  {formErrors.gross && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('CONTACTS:IMPORT_NET')} *</Label>
                  <InputGroup>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      name="net"
                      value={net}
                      onChange={onNumericInputChange}
                      disabled={insurance.panoramicaInvoiceId !== undefined}
                    />
                    <InputGroupAddon addonType="prepend">€</InputGroupAddon>
                  </InputGroup>
                  {formErrors.net && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('PANORAMICA_ROY')} *</Label>
                  <InputGroup>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      name="shareToInvoice"
                      value={shareToInvoice}
                      onChange={onNumericInputChange}
                      disabled={insurance.panoramicaInvoiceId !== undefined}
                    />
                    <InputGroupAddon addonType="prepend">€</InputGroupAddon>
                  </InputGroup>
                  {formErrors.shareToInvoice && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6" lg="4">
                <FormGroup>
                  <Label>{t('SUB_AGENCY_PERC')} *</Label>
                  <InputGroup>
                    <Input
                      type="number"
                      placeholder="0"
                      name="subAgencyPerc"
                      value={subAgencyPerc}
                      onChange={onNumericInputChange}
                      disabled={insurance.panoramicaInvoiceId !== undefined}
                    />
                    <InputGroupAddon addonType="prepend">%</InputGroupAddon>
                  </InputGroup>
                  {formErrors.subAgencyPerc && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col xs="12" md="6">
                <FormGroup>
                  <Label>{t('AMMINISTRATOR')} *</Label>
                  <AutoSuggest
                    placeholder="Cerca un amministratore..."
                    onFetch={searchAdministrators}
                    onSelect={onAdminSuggestionSelect}
                    renderSuggestion={renderAdminSuggestion}
                    getSuggestionValue={getAdminSuggestionValue}
                    value={admin.businessName}
                    onChange={(businessName) => {
                      if (businessName === '') {
                        setFormValues((state) => ({
                          ...state,
                          admin: {
                            _id: '',
                            businessName: '',
                          },
                          customer: '',
                          vatCode: '',
                          customerType: '',
                          customerId: '',
                        }));
                      } else {
                        setFormValues((state) => ({
                          ...state,
                          admin: { ...admin, businessName },
                        }));
                      }
                    }}
                    disabled={
                      insurance.panoramicaInvoiceId !== undefined ||
                      start_date === undefined ||
                      start_date === '' ||
                      formValues.estateAgent.businessName !== '' ||
                      formValues.commercial.businessName !== ''
                    }
                  />
                  {formErrors.admin && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>
              <Col xs="12" md="6">
                <FormGroup>
                  <Label>{t('AGENCY')} *</Label>
                  <AutoSuggest
                    placeholder="Cerca un agenzia..."
                    onFetch={searchEstateAgents}
                    onSelect={onEstateAgentsSuggestionSelect}
                    renderSuggestion={renderAdminSuggestion}
                    getSuggestionValue={getAdminSuggestionValue}
                    value={estateAgent.businessName}
                    onChange={(businessName) => {
                      if (businessName === '') {
                        setFormValues((state) => ({
                          ...state,
                          estateAgent: {
                            _id: '',
                            businessName: '',
                          },
                          customer: '',
                          vatCode: '',
                          customerType: '',
                          customerId: '',
                        }));
                      } else {
                        setFormValues((state) => ({
                          ...state,
                          estateAgent: { ...estateAgent, businessName },
                        }));
                      }
                    }}
                    disabled={
                      insurance.panoramicaInvoiceId !== undefined ||
                      start_date === undefined ||
                      start_date === '' ||
                      formValues.admin.businessName !== '' ||
                      formValues.commercial.businessName !== ''
                    }
                  />
                  {formErrors.estateAgent && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>
              <Col xs="12" md="6">
                <FormGroup>
                  <Label>{t('COMMERCIAL')} *</Label>
                  <AutoSuggest
                    placeholder="Cerca un commerciale..."
                    onFetch={searchCommercials}
                    onSelect={onCommercialsSuggestionSelect}
                    renderSuggestion={renderAdminSuggestion}
                    getSuggestionValue={getAdminSuggestionValue}
                    value={commercial.businessName}
                    onChange={(businessName) => {
                      if (businessName === '') {
                        setFormValues((state) => ({
                          ...state,
                          commercial: {
                            _id: '',
                            businessName: '',
                          },
                          customer: '',
                          vatCode: '',
                          customerType: '',
                          customerId: '',
                        }));
                      } else {
                        setFormValues((state) => ({
                          ...state,
                          commercial: { ...commercial, businessName },
                        }));
                      }
                    }}
                    disabled={
                      insurance.panoramicaInvoiceId !== undefined ||
                      start_date === undefined ||
                      start_date === '' ||
                      formValues.admin.businessName !== '' ||
                      formValues.estateAgent.businessName !== ''
                    }
                  />
                  {formErrors.commercial && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col xs="12" md="6">
                <FormGroup>
                  <Label>{t('CONDO')} *</Label>
                  <CustomInput
                    type="select"
                    id="type"
                    name="customer"
                    value={
                      customer !== ''
                        ? condos.find((condo) => condo._id === customer)?._id
                        : ''
                    }
                    onChange={(e) => onSelectCondo(e.target.value)}
                    disabled={
                      insurance.panoramicaInvoiceId !== undefined ||
                      formValues.customerType === 'customerExtra'
                        ? true
                        : formValues.estateAgent._id !== '' ||
                          formValues.commercial._id !== ''
                        ? true
                        : false
                    }
                  >
                    <option value="">{t('SELECT_CONDO')}</option>
                    {condos.map((condo) => (
                      <option key={condo._id} value={condo._id}>
                        {`${condo.name} - ${condo.vatCode}`}
                      </option>
                    ))}
                  </CustomInput>
                  {formValues.estateAgent._id === '' &&
                    formValues.admin._id === '' &&
                    formValues.commercial._id === '' && (
                      <FormText className="error-message">
                        {t('SELECT_ADMIN_ERROR')}
                      </FormText>
                    )}
                  {formValues.admin._id !== '' && condos.length === 0 && (
                    <FormText className="error-message">
                      {t('NO_CONDO_ERROR')}
                    </FormText>
                  )}
                  {formErrors.customer && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>

              <Col xs="12" md="6">
                <FormGroup>
                  <Label>{t('CUSTOMER_EXTRA')} *</Label>
                  <CustomInput
                    type="select"
                    id="type"
                    name="customerExtra"
                    value={
                      customer !== ''
                        ? customersExtra.find(
                            (customerExtra) => customerExtra._id === customer
                          )?._id
                        : ''
                    }
                    onChange={(e) => onSelectCustomerExtra(e.target.value)}
                    disabled={
                      insurance.panoramicaInvoiceId !== undefined ||
                      formValues.customerType === 'condos' ? true : false
                    }
                  >
                    <option value="">{t('SELECT_CUSTOMER')}</option>
                    {customersExtra.map((customer) => (
                      <option key={customer._id} value={customer._id}>
                        {`${
                          customer.businessName && customer.businessName !== ''
                            ? customer.businessName
                            : customer.name + ' ' + customer.surname
                        } - ${customer.taxCode}`}
                      </option>
                    ))}
                  </CustomInput>
                  {formValues.estateAgent._id === '' &&
                    formValues.admin._id === '' &&
                    formValues.commercial._id === '' && (
                      <FormText className="error-message">
                        {t('SELECT_AGENCY_CUSTOMER_ERROR')}
                      </FormText>
                    )}
                  {(formValues.estateAgent._id !== '' ||
                    formValues.commercial._id !== '') &&
                    customersExtra.length === 0 && (
                      <FormText className="error-message">
                        {t('NO_CUSTOMER_ERROR')}
                      </FormText>
                    )}
                  {formErrors.customer && (
                    <FormText className="error-message">
                      {t('COMMON:REQUIRED')}
                    </FormText>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <BtnOutlineRed type="button" className="uppercase" onClick={toggle}>
              {t('COMMON:CANCEL')}
            </BtnOutlineRed>
            <BtnOutlineGreen type="submit" className="uppercase">
              {t('COMMON:CONFIRM')}
            </BtnOutlineGreen>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default ModalEditInsurance;
